import { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ImageCarousel, VideoCarousel } from '@spotahome/ui-library';

import { getModifier } from './utils/getSliderComponent';
import { IMG_TYPE, VIDEO_TYPE } from './utils/constants';

import SliderTrack from './SliderTrack';
import SingleSliderTrack from './SingleSliderTrack';
import CarouselItemsWrapper from './CarouselItemsWrapper';

import './SliderGallery.scss';

const dimensionPropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number
]);

const TRACK_TYPE_CORRELATIONS = {
  // eslint-disable-next-line react/prop-types
  [IMG_TYPE]: ({ items, ref }) => (
    <ImageCarousel
      items={items}
      chevronStyle="big"
      ratio={ImageCarousel.RATIOS.PANORAMIC}
      keyboardHandleEnabled
      showCounter
      showCaption
      ref={ref}
      preloadRange={2}
      autoFocus
    />
  ),
  [VIDEO_TYPE]: ({ items, type, ref, currentTrackItem }) =>
    items.length > 1 ? (
      <VideoCarousel
        items={items}
        chevronStyle="big"
        ratio={ImageCarousel.RATIOS.PANORAMIC}
        keyboardHandleEnabled
        ref={ref}
        showCounter
        keepHeightWhileLoading
      />
    ) : (
      <SliderTrack
        ref={ref}
        type={type}
        items={items}
        currentTrackItem={currentTrackItem}
      />
    ),
  // eslint-disable-next-line react/prop-types
  default: ({ items, type, ref, currentTrackItem }) => (
    <SliderTrack
      ref={ref}
      type={type}
      items={items}
      currentTrackItem={currentTrackItem}
    />
  )
};

const SliderGallery = ({
  height = 'auto',
  width = 'auto',
  isLandscape = false,
  tracks,
  defaultTrackToShow,
  onTrackChange,
  counterRef,
  carouselRef,
  currentTrackItem,
  listingId
}) => {
  const [currentTrack, setCurrentTrack] = useState(defaultTrackToShow);
  const selectedTrack = tracks[currentTrack];

  useEffect(() => {
    onTrackChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack]);

  const galleryClassName = classNames('slider-gallery', {
    'slider-gallery--is-vertical': isLandscape
  });

  const getGalleryStyles = () => {
    const { type } = tracks[currentTrack];
    const galleryModifier = getModifier(type);
    const galleryStyles = {
      height,
      width
    };

    return { ...galleryStyles, ...galleryModifier };
  };

  const handleTrackSelection = i => {
    setCurrentTrack(i);
  };

  const renderTrackItem =
    TRACK_TYPE_CORRELATIONS[selectedTrack.type] ||
    TRACK_TYPE_CORRELATIONS.default;

  return (
    <Fragment>
      <div className={galleryClassName} style={getGalleryStyles()}>
        {selectedTrack.items.length > 1 ? (
          renderTrackItem({
            items: selectedTrack.items.map(item => ({
              ...item,
              listingId
            })),
            type: selectedTrack.type,
            ref: counterRef,
            currentTrackItem
          })
        ) : (
          <SingleSliderTrack
            type={selectedTrack.type}
            item={{
              ...selectedTrack.items[0],
              listingId
            }}
          />
        )}
      </div>
      <CarouselItemsWrapper
        carouselRef={carouselRef}
        onClick={handleTrackSelection}
        trackData={tracks}
        isFullScreen
        isVertical={isLandscape}
        selectedTrack={currentTrack}
        carouselHeight={height}
        carouselWidth={width}
      />
    </Fragment>
  );
};

SliderGallery.propTypes = {
  height: dimensionPropType,
  width: dimensionPropType,
  isLandscape: PropTypes.bool,
  tracks: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  defaultTrackToShow: PropTypes.number.isRequired,
  onTrackChange: PropTypes.func.isRequired,
  counterRef: PropTypes.shape({}).isRequired,
  carouselRef: PropTypes.shape({}).isRequired,
  currentTrackItem: PropTypes.number.isRequired,
  listingId: PropTypes.string.isRequired
};

export default SliderGallery;
